import Reac from "react"
import { IAxiosDefaultDataObject } from "../config"
import api from "../services/api"
import { IAdminPageMessages, ICreateOrUpdatePageProps } from "./admin/Interfaces"
import { DataDisplayerItemFieldTypes, IDataDisplayerHeaderOptionActionButton, IDataDisplayerItemField, IDataDisplayerNothingFound, IDataDisplayerPagination, IDataDisplyerItemActionOption } from "../components/DataDisplayer/DataDisplayer"
import { IDynamicForm, IRequiredField } from "../components/Form/DynamicForm"
import { Dispatch, UnknownAction } from "@reduxjs/toolkit"
import { IAdminDynamicBasePageProps } from "./admin/AdminDynamicBasePage"
import { IPageHeadSectionActionButton } from "./admin/components/PageHeadSection"
import { GridDataColumns, IGridDataCellActionButton, IGridDataConfig, IGridDataEssentials, IGridHeaderOption } from "../components/GridData"
import DynamicElementQuickPreview from "./admin/DynamicElementQuickPreview"

export interface IBaseAppPages {
    createOrUpdate?: React.FC<ICreateOrUpdatePageProps>
}

export interface ISearchParam {
    name: string,
    value: any
}

export interface IBaseAppDataDisplayerColumn {
    id: string,
    type: DataDisplayerItemFieldTypes,
    label: string,
    maxWidth?: number,
    minWidth?: number,
    quickItemView?: (item: IAxiosDefaultDataObject, shown: boolean, handleHide: () => void) => JSX.Element,
    customContent?: (item: IAxiosDefaultDataObject) => JSX.Element
}

export interface IBaseAppDataDisplayerConfig {
    columns: Array<IBaseAppDataDisplayerColumn>,
    notionFound: IDataDisplayerNothingFound
}

export interface IBaseAppFormConfig {
    form: IDynamicForm,
    sectionBox: {
        showTitle?: boolean,
        title?: string,
        description?: string
    },
    requiredFields: Array<IRequiredField>
}

export interface IBaseAppDynamicBasePage {
    items: () => IAxiosDefaultDataObject[],
    pagination: () => IDataDisplayerPagination,
    loading: () => boolean,
    search: () => string | undefined,
    updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => void,
    deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => void,
    updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => void,
    addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => void,
    updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => void,
    updateAllItems: (items: IAxiosDefaultDataObject[], dispatch: Dispatch<UnknownAction>) => void,
    updateItemsPagination: (pagination: IDataDisplayerPagination, dispatch: Dispatch<UnknownAction> ) => void,
    itemsActionOptions: IDataDisplyerItemActionOption[],
    enableSubItems?: boolean,
    subItemsElemId?: string,
    subItemsColumns?: IBaseAppDataDisplayerColumn[],
    subItemsActionOptions?: Array<IDataDisplyerItemActionOption>,
    filterForm?: IDynamicForm,
    customHeaderOptionActionButtons?: IDataDisplayerHeaderOptionActionButton[],
    viewItemTemplateLink?: string,
    disableAutoViewItemLink?: boolean,
    hidePageHeadSection?: boolean,
    customTitlePageHeadSection?: string,
    disableSearch?: boolean,
    customPageHeadSectionActionButtons?: IPageHeadSectionActionButton[]
}

export interface IGridDataAdditional<T = any> {
    selectedItems: () => T[],
    searchParams: () => ISearchParam[],
    actionButtons: IGridDataCellActionButton[],
    headerOptions?: IGridHeaderOption[],
    setSelectedItems: (items: T, dispatch: Dispatch<UnknownAction>) => void,
    updateItemSearchParams: (params: ISearchParam[], dispatch: Dispatch<UnknownAction>) => void,
    updateSearchParamsBeforeLoadItems?: (params: ISearchParam[], dispatch: Dispatch<UnknownAction>) => ISearchParam[] 
}

export interface IBaseApp {
    appName: string,
    appBaseEndPoint: string,
    pages?: IBaseAppPages,
    createNewInstance: (data: IAxiosDefaultDataObject) => Promise<any>,
    singleInstanceDetails: (itemId: number) => Promise<any>,
    listOfInstances: (page: number, search: string|null,  disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => Promise<any>,
    deleteInstance: (id: number) =>  Promise<any>,
    deleteManyInstances: (records: IAxiosDefaultDataObject, permanentlyDelete?: boolean) =>  Promise<any>,
    dataDisplayer: IBaseAppDataDisplayerConfig,
    formConfig: IBaseAppFormConfig,
    elemPrefix: string,
    customListEndPoint?: string,
    messages: IAdminPageMessages,
    redux?: {
        updateItem?: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => void,
        addNewItem?: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => void
    },
    dynamicBasePage?: IBaseAppDynamicBasePage,
    gridDataColumns?: GridDataColumns,
    gridConfig?: IGridDataConfig,
    gridDataEssentials?: IGridDataAdditional,
    useGridData?: boolean,
    isBeta?: boolean,
    quickPreview?: (id: number) => JSX.Element
}

/**
 * Replace item fields in the link
 */
export const replaceItemDataInLink = (link: string, item: IAxiosDefaultDataObject) => {

    let formattedLink: string = link

    try{
        formattedLink = link.replace(/\[([^\]]+)\]/g, (_, key) => {
            return item[key] || `[${key}]`; 
        })
    }catch(error){
        console.log(error)
    }

    return formattedLink
}

/**
 * Base App
 */
export default class BaseApp implements IBaseApp {

    appName = ""
    appBaseEndPoint = ""
    elemPrefix = "prefix_"
    customListEndPoint = ""

    messages = {
        create: "Create a new",
        createShort: "New",
        edit: "Edit",
        editShort: "Edit",
        delete: "Delete",
        deleteShort: "Delete",
        view: "View",
        nothingFound: "Nothing found..."
    }

    quickPreview =(id: number) => {
        return <DynamicElementQuickPreview elemId={id} pageName={this.appName} />
    }

    /**
     * Create new instance
     */
    createNewInstance = async(data: IAxiosDefaultDataObject) => {
        const response = await api.put(this.customListEndPoint ? this.customListEndPoint : this.appBaseEndPoint + "/", data)
        return response
    }

    /**
     * Get single instance details
     */
    singleInstanceDetails = async(itemId: number) => {
        return await api.get(this.appBaseEndPoint + "/" + itemId)
    }

    /**
     * Delete instance
     */
    deleteInstance = async(id: number) => {
        return await api.delete(this.appBaseEndPoint + "/" + id)
    }

    /**
     * Delete instance
     */
    deleteManyInstances = async(records: IAxiosDefaultDataObject, permanentlyDelete?: boolean) => {
        return await api.delete(this.appBaseEndPoint + "/delete-many", { data:{
            records: records,
            permanently_delete: permanentlyDelete,
        } })
    }

    /**
     * List of instances
     */
    listOfInstances = async(page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }

        console.log("searchParams", searchParams)

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [],
        notionFound: {
            title: "Nothing...",
            message: "Nothing has been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "test",
            elements: []
        },
        sectionBox: {
            showTitle: false,
            title: "",
            description: ""
        },
        requiredFields: []
    }

    /**
     * Grid data
     */
    useGridData: boolean = false
    gridDataColumns: GridDataColumns = [
        {
            field: "thumbnail",
            headerName: "Thumbnail",
            type: "thumbnail",
            resizable: false,
            width: 110,
        },
        {
            field: "title",
            headerName: "Title",
            minWidth: 300
        }

    ]

    gridConfig: IGridDataConfig = {
        viewItemLink: `/panel/${this.appName}/view/[id]`,
        editItemLink: `/panel/${this.appName}/edit/[id]`,
        hideFilters: true,
    }

    dynamicBasePage?: IBaseAppDynamicBasePage = undefined;

}